import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { HubFormCard, HubSheet, HubSheetActions } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import { PATIENTS_BASE } from "./patientConstants";
import L from "../../../constants/baysportLabels";
import { ageFromDob } from "../../../utils/clientAppUtils";
import { Button, Field, Input, Select, useToast } from "../../ui";

const EMPTY_CHILD = { fullName: "", date_of_birth: "" };

function ClientInviteOptions({
  sendInviteEmail,
  onSendInviteEmailChange,
  hasEmail,
  hasPhone,
  solo = false,
}) {
  const who = solo ? "клиенту" : "родителю";
  return (
    <div className={aa.clientAddInviteBlock}>
      <p className={aa.clientAddSheetNote}>
        После сохранения {who} уйдёт ссылка для активации личного кабинета
        {hasPhone ? " по SMS" : ""}
        {hasPhone && hasEmail ? " и" : ""}
        {hasEmail ? " на email" : ""}
        {!hasPhone && !hasEmail ? " — скопируйте ссылку вручную" : ""}.
      </p>
      {hasEmail ? (
        <label className={aa.clientAddInviteCheck}>
          <input
            type="checkbox"
            checked={sendInviteEmail}
            onChange={(e) => onSendInviteEmailChange(e.target.checked)}
          />
          <span>Отправить приглашение на email</span>
        </label>
      ) : null}
    </div>
  );
}

function ChildDobField({ label, value, onChange, required }) {
  const age = ageFromDob(value);
  return (
    <Field
      label={label}
      hint={age != null ? `Возраст: ${age} лет` : undefined}
    >
      <Input
        type="date"
        required={required}
        value={value}
        onChange={onChange}
        max={new Date().toISOString().slice(0, 10)}
      />
    </Field>
  );
}

export default function ClientAddSheet({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isFamily, setIsFamily] = useState(false);
  const [sendInviteEmail, setSendInviteEmail] = useState(true);
  const [children, setChildren] = useState([{ ...EMPTY_CHILD }]);
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    date_of_birth: "",
    parentName: "",
    parentPhone: "",
    parentEmail: "",
    group_id: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      fullName: "",
      phone: "",
      email: "",
      date_of_birth: "",
      parentName: "",
      parentPhone: "",
      parentEmail: "",
      group_id: "",
    });
    setChildren([{ ...EMPTY_CHILD }]);
    setIsFamily(false);
    setSendInviteEmail(true);
    clinicAPI
      .getSportGroupsSummary()
      .then((r) => setGroups(r.groups || []))
      .catch(() =>
        clinicAPI.getServicesCatalog().then((c) => setGroups(c.specializations || [])),
      );
  }, [open]);

  const contactEmail = isFamily ? form.parentEmail : form.email;
  const contactPhone = isFamily ? form.parentPhone : form.phone;
  const hasEmail = Boolean(contactEmail.trim());
  const hasPhone = Boolean(contactPhone.trim());

  const notifyInviteResult = (r, fallbackMessage) => {
    if (r.invite_url && !r.invite_email_sent && !r.invite_sms_sent) {
      return navigator.clipboard
        .writeText(r.invite_url)
        .then(() => toast.success(`${fallbackMessage}. Ссылка активации скопирована`))
        .catch(() => toast.success(`${fallbackMessage}. Отправьте ссылку из карточки`));
    }
    toast.success(r.message || fallbackMessage);
  };

  const patchChild = (idx, patch) => {
    setChildren((list) =>
      list.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    );
  };

  const submitSolo = async () => {
    if (!form.fullName.trim()) {
      toast.error("Укажите имя клиента");
      return;
    }
    if (!form.phone.trim()) {
      toast.error("Укажите телефон клиента");
      return;
    }

    const r = await clinicAPI.createPatient({
      fullName: form.fullName.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || undefined,
      date_of_birth: form.date_of_birth || undefined,
      password: "",
      group_id: form.group_id || undefined,
      send_invite: true,
      send_invite_email: hasEmail && sendInviteEmail,
      send_invite_sms: hasPhone,
    });
    await notifyInviteResult(r, "Клиент добавлен");
    onClose?.();
    const patientId = r.patient?.id;
    if (patientId) {
      onCreated?.(patientId);
      navigate(`${PATIENTS_BASE}/${patientId}`);
    } else {
      onCreated?.();
    }
  };

  const submitFamily = async () => {
    if (!form.parentName.trim()) {
      toast.error("Укажите имя родителя");
      return;
    }
    if (!form.parentPhone.trim()) {
      toast.error("Укажите телефон родителя");
      return;
    }
    const childRows = children
      .map((c) => ({
        fullName: c.fullName.trim(),
        date_of_birth: c.date_of_birth || null,
      }))
      .filter((c) => c.fullName);
    if (!childRows.length) {
      toast.error("Добавьте хотя бы одного ребёнка");
      return;
    }
    if (childRows.some((c) => !c.date_of_birth)) {
      toast.error("Укажите дату рождения каждого ребёнка");
      return;
    }

    const r = await clinicAPI.createClientFamily({
      parent: {
        fullName: form.parentName.trim(),
        phone: form.parentPhone.trim(),
        email: form.parentEmail.trim() || undefined,
      },
      children: childRows,
      group_id: form.group_id || undefined,
      send_invite: true,
      send_invite_email: hasEmail && sendInviteEmail,
      send_invite_sms: hasPhone,
    });
    await notifyInviteResult(r, "Семья создана");
    onClose?.();
    const athleteId = r.athletes?.[0]?.id;
    if (athleteId) {
      onCreated?.(athleteId);
      navigate(`${PATIENTS_BASE}/${athleteId}`);
    } else {
      onCreated?.();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isFamily) await submitFamily();
      else await submitSolo();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка создания");
    } finally {
      setSaving(false);
    }
  };

  const canSave = isFamily
    ? form.parentName.trim() &&
      form.parentPhone.trim() &&
      children.some((c) => c.fullName.trim() && c.date_of_birth)
    : form.fullName.trim() && form.phone.trim();

  return (
    <HubSheet
      open={open}
      onClose={onClose}
      title="Новый клиент"
      subtitle={
        isFamily
          ? "Родитель и один или несколько детей"
          : "Один спортсмен — телефон и имя"
      }
      titleId="client-add-sheet-title"
      bodyClassName={aa.clientAddSheetBody}
      footer={
        <HubSheetActions
          primary={saving ? "Сохранение…" : "Сохранить"}
          primaryForm="client-add-form"
          primaryDisabled={saving || !canSave}
          onSecondary={onClose}
        />
      }
    >
      <form id="client-add-form" className={aa.clientAddSheetForm} onSubmit={submit}>
        <button
          type="button"
          className={`${aa.clientAddSheetParentToggle} ${
            isFamily ? aa.clientAddSheetParentToggleOn : ""
          }`}
          onClick={() => setIsFamily((v) => !v)}
          aria-pressed={isFamily}
        >
          <span className={aa.clientAddSheetParentToggleIcon} aria-hidden>
            👨‍👩‍👧
          </span>
          <span className={aa.clientAddSheetParentToggleText}>
            <span className={aa.clientAddSheetParentToggleTitle}>
              {isFamily ? "Семейный аккаунт" : "Добавить семью"}
            </span>
            <span className={aa.clientAddSheetParentToggleHint}>
              {isFamily
                ? "Родитель и один или несколько детей"
                : "Если клиент — ребёнок с родителем, включите семейный режим"}
            </span>
          </span>
          <span className={aa.clientAddSheetParentToggleChevron} aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {isFamily ? (
          <>
            <HubFormCard title="Родитель">
              <Field label="Имя родителя">
                <Input
                  required
                  autoFocus
                  value={form.parentName}
                  onChange={(e) => setForm({ ...form, parentName: e.target.value })}
                  placeholder="Мария Иванова"
                />
              </Field>
              <Field label="Телефон родителя" hint="Уникальный идентификатор семьи в клубе">
                <Input
                  type="tel"
                  inputMode="tel"
                  required
                  value={form.parentPhone}
                  onChange={(e) => setForm({ ...form, parentPhone: e.target.value })}
                  placeholder="+7 …"
                />
              </Field>
              <Field
                label="Email родителя"
                hint="Необязательно — для восстановления доступа и рассылок"
              >
                <Input
                  type="email"
                  value={form.parentEmail}
                  onChange={(e) => setForm({ ...form, parentEmail: e.target.value })}
                  placeholder="parent@example.com"
                />
              </Field>
              <ClientInviteOptions
                hasEmail={hasEmail}
                hasPhone={hasPhone}
                sendInviteEmail={sendInviteEmail}
                onSendInviteEmailChange={setSendInviteEmail}
              />
            </HubFormCard>

            <HubFormCard title="Дети">
              {children.map((child, idx) => (
                <div key={idx}>
                  <Field label={idx === 0 ? "Имя ребёнка" : `Ребёнок ${idx + 1}`}>
                    <Input
                      required={idx === 0}
                      value={child.fullName}
                      onChange={(e) => patchChild(idx, { fullName: e.target.value })}
                      placeholder="Иван Иванов"
                    />
                  </Field>
                  <ChildDobField
                    label="Дата рождения"
                    required={idx === 0}
                    value={child.date_of_birth}
                    onChange={(e) => patchChild(idx, { date_of_birth: e.target.value })}
                  />
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setChildren((list) => [...list, { ...EMPTY_CHILD }])}
              >
                + Ещё ребёнок
              </Button>
              <Field label={L.group}>
                <Select
                  value={form.group_id}
                  onChange={(e) => setForm({ ...form, group_id: e.target.value })}
                >
                  <option value="">Без группы</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </HubFormCard>
          </>
        ) : (
          <HubFormCard title="Клиент">
            <Field label="Имя">
              <Input
                required
                autoFocus
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                placeholder="Иван Иванов"
              />
            </Field>
            <Field label="Телефон" hint="Уникальный идентификатор в клубе">
              <Input
                type="tel"
                inputMode="tel"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+7 …"
              />
            </Field>
            <Field
              label="Email"
              hint="Необязательно — для восстановления доступа и рассылок"
            >
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="client@example.com"
              />
            </Field>
            <ChildDobField
              label="Дата рождения"
              value={form.date_of_birth}
              onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
            />
            <ClientInviteOptions
              solo
              hasEmail={hasEmail}
              hasPhone={hasPhone}
              sendInviteEmail={sendInviteEmail}
              onSendInviteEmailChange={setSendInviteEmail}
            />
            <Field label={L.group}>
              <Select
                value={form.group_id}
                onChange={(e) => setForm({ ...form, group_id: e.target.value })}
              >
                <option value="">Без группы</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </Field>
          </HubFormCard>
        )}
      </form>
    </HubSheet>
  );
}
